import type { CdwGridState } from './cdw-grid-state.interface';

export enum ExportStatus {
  CREATED = 'CREATED',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED',
  ERROR = 'ERROR',
}

export enum ExportPurpose {
  HUMAN_READABLE = 'human-readable',
  IMPORT_READY = 'import-ready',
}

export enum ExportFormat {
  GOOGLE_SHEETS = 'google-sheets',
  CSV = 'csv',
}

export type ExportQueryType =
  | 'device-search'
  | 'skipped-devices'
  | 'audit-log-changes'
  | 'device-audit-history';

export interface ExportSidecarColumn {
  name: string;
  displayName: string;
  values: Record<string, string>;
}

export interface ExportCreationRequest {
  gridState: CdwGridState;
  purpose: ExportPurpose;
  queryType: ExportQueryType;
  format: ExportFormat;
  fileName: string;
  recipients?: string[];
  customerId: number;
  /**
   * Firebase UID of the requesting user. Flows downstream as the
   * `x-cdw-firebase-user-id` header the export job steps use to resolve the
   * user's Redis selection set. NOT job tracking — see `dbUserId`.
   */
  fbUserId: string;
  /** Integer DB user ID. Used for job tracking so the export appears in My Jobs. */
  dbUserId?: number;
  userEmail: string;
  userName?: string;
  sidecarColumns?: ExportSidecarColumn[];
  /**
   * EDUS-2486 Hit-list #7: when true, the saas-device-api `/exports`
   * controller resolves the affected device id list from the user's
   * Redis selection set and injects it into `gridState.selectedRowIds`
   * before dispatching the export job. Requires `viewKey` in this
   * payload and the `x-cdw-grid-session` + `x-cdw-firebase-user-id`
   * headers (forwarded through spog-backend → asset-service-layer →
   * saas-device-api). The downstream Cloud Workflow + job steps are
   * unchanged — they continue to use the materialized
   * `gridState.selectedRowIds` codepath.
   */
  useSelection?: boolean;
  /** Required when `useSelection: true` — identifies the grid view's selection key. */
  viewKey?: string;
  /**
   * EDUS-2698: source audit log id for the `audit-log-changes` query type.
   * When set, the export dispatches the `audit-log-changes-export-workflow`,
   * whose change-diff export steps resolve this audit log's source
   * device-update job and emits per-device before/after field diffs.
   * Threaded verbatim through spog-backend → asset-service-layer →
   * saas-device-api into the export job's `configuration.json`.
   */
  auditLogId?: number;
  /**
   * EDUS-2699: target asset for the `device-audit-history` query type — the
   * INTERNAL asset uuid (e.g. `asset_devices.id`, the id the device-summary
   * page holds). When set, the export dispatches the
   * `device-audit-history-export-workflow`, whose steps query this asset's full
   * audit-change history and emit one before/after diff row per change event.
   * Threaded verbatim through spog-backend → asset-service-layer →
   * saas-device-api into the export job's `configuration.json`.
   */
  assetId?: string;
  /** EDUS-2699: asset type for `assetId` (1 = Device). Defaults to Device downstream. */
  assetTypeId?: number;
}

export interface ExportApiResponse {
  success: boolean;
  message?: string;
  exportId?: string;
}
